import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Activity, BarChart3 } from "lucide-react";
import { buildDeviceLabelMap, groupDevicesByHardware } from "@/lib/device-labels";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ErrorDisplay } from "@/components/loading";
import { SensorTable } from "@/components/sensor-table";
import { UploadAlertDialog } from "@/components/sensor-charts/upload-alert-dialog";
import { useUserDevices, useParticipants } from "@/api/hooks";
import { formatDate, relativeTime } from "@/lib/format-date";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/auth-context";
import { ParticipantDiagnostics } from "@/components/diagnostics/participant-diagnostics";
import {
  getUploadAlertBadgeLabel,
  getUploadAlertDescription,
  getUploadAlertTitle,
  getUploadAlertWindowLabel,
  hasUploadAlert,
} from "@/lib/upload-alerts";

export default function ParticipantDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { isAdmin } = useAuth();
  const devices = useUserDevices(id ?? "");
  const participants = useParticipants();
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const [alertDialogOpen, setAlertDialogOpen] = useState(false);

  const participant = useMemo(
    () => participants.data?.data.find((p) => p.user_id === id),
    [participants.data, id],
  );

  const username = participant?.username || id?.slice(0, 8) || "";
  const totalUploads = participant?.total_uploads ?? 0;
  const lastSeen = participant?.last_uploaded_at || null;
  const activeSources = participant?.sources?.length ?? 0;
  const hasAlert = participant ? hasUploadAlert(participant) : false;

  const rawDeviceGroups = useMemo(
    () => groupDevicesByHardware(devices.data?.data ?? []),
    [devices.data],
  );

  // Device registration is the authoritative first-seen time. Client events
  // may be backfilled with collected_at values from before enrollment.
  const firstSeen = useMemo(() => {
    const deviceFirstSeen = rawDeviceGroups
      .map((group) => group.firstSeen)
      .filter(Boolean)
      .sort()[0];
    return deviceFirstSeen || participant?.first_seen || null;
  }, [participant?.first_seen, rawDeviceGroups]);

  // When the participant has exactly one logical device, the group's last activity is
  // the server-observed upload time rather than the phone-provided event time or the
  // /devices/register check-in, both of which can be skewed or lag behind.
  const deviceGroups = useMemo(() => {
    if (rawDeviceGroups.length === 1 && participant?.last_uploaded_at) {
      const g = rawDeviceGroups[0];
      return [{ ...g, lastSeen: participant.last_uploaded_at }];
    }
    return rawDeviceGroups;
  }, [rawDeviceGroups, participant]);

  const deviceLabels = useMemo(
    () => buildDeviceLabelMap(devices.data?.data ?? []),
    [devices.data],
  );

  if (!id) return <ErrorDisplay message="No participant ID provided" />;

  const gapWindowLabel = participant ? getUploadAlertWindowLabel(participant) : null;

  return (
    <div className="p-4 space-y-6 md:p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-semibold text-[#1E293B]">
          Participant <span className="font-mono text-lg">{username}</span>
        </h1>
        {participant && (
          <Button variant="outline" onClick={() => setAlertDialogOpen(true)}>
            <BarChart3 className="mr-1.5 size-4" />
            Analyze collection
          </Button>
        )}
      </div>

      {hasAlert && participant && (
        <Card className="border-amber-300 bg-amber-50/60">
          <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="space-y-1">
              <div className="font-medium text-amber-900">
                {getUploadAlertTitle(participant)}
              </div>
              <div className="text-sm text-amber-900/80">
                {getUploadAlertDescription(participant)}
              </div>
              {gapWindowLabel && (
                <div className="font-mono text-xs text-amber-900">
                  Gap window: {gapWindowLabel}
                </div>
              )}
            </div>
            <Badge className="bg-amber-100 text-amber-900 border-amber-300">
              {getUploadAlertBadgeLabel(participant)}
            </Badge>
          </CardContent>
        </Card>
      )}

      <UploadAlertDialog
        participant={participant ?? null}
        open={alertDialogOpen}
        onOpenChange={setAlertDialogOpen}
        showParticipantLink={false}
      />

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Uploads
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalUploads.toLocaleString()}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Last Seen
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-lg font-medium">
              {lastSeen ? relativeTime(lastSeen) : "—"}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground">
              First Seen
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-lg font-medium">
              {firstSeen ? formatDate(firstSeen) : "—"}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Active Sources
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{activeSources}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Devices
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{deviceGroups.length}</div>
          </CardContent>
        </Card>
      </div>

      {(deviceGroups.length > 0 || isAdmin) && (
        <div className="space-y-3">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Devices
          </h2>
          {deviceGroups.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Device</TableHead>
                <TableHead>Manufacturer</TableHead>
                <TableHead>Model</TableHead>
                <TableHead>Android</TableHead>
                <TableHead>App Version</TableHead>
                <TableHead>First Seen</TableHead>
                <TableHead>Last Seen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {deviceGroups.map((group) => (
                <TableRow key={group.deviceIds.join("|")}>
                  <TableCell className="text-xs font-medium">{group.label}</TableCell>
                  <TableCell className="capitalize">{group.manufacturer || "—"}</TableCell>
                  <TableCell>{group.model || "—"}</TableCell>
                  <TableCell>{group.androidVersion || "—"}</TableCell>
                  <TableCell>{group.latestAppVersion || "—"}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {group.firstSeen ? formatDate(group.firstSeen) : "—"}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {group.lastSeen ? relativeTime(group.lastSeen) : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          ) : (
            <p className="text-sm text-muted-foreground">No devices registered.</p>
          )}

          {isAdmin && (
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowDiagnostics((visible) => !visible)}
              aria-expanded={showDiagnostics}
            >
              <Activity className="size-4" />
              {showDiagnostics ? "Hide diagnostics" : "Open diagnostics"}
            </Button>
          )}
        </div>
      )}

      {isAdmin && showDiagnostics && (
        <ParticipantDiagnostics
          userId={id}
          devices={devices.data?.data ?? []}
          active={showDiagnostics}
        />
      )}

      <div className="space-y-2">
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
          Sensor Data
        </h2>
        <SensorTable
          participantId={id}
          hideParticipantFilter
          hideParticipantColumn
          showVisualization
          deviceLabels={deviceLabels}
        />
      </div>

    </div>
  );
}
